// BookTickets/stages/DateAndTime/hooks/useSessionsForDate.ts
'use client'

import { useEffect, useState } from 'react'
import { format as formatDate } from 'date-fns'

import type { Session } from '@booking/types/booking'
import { apiGetSessionsByDate } from '@booking/services/bookingApi'

type UseSessionsForDateArgs = {
  date: Date
  enabled: boolean
  sessionTags?: string[]
}

type UseSessionsForDateResult = {
  loading: boolean
  sessions: Session[]
  refresh: () => Promise<void>
}

/**
 * Fetches sessions for a date and applies basic “future only” filtering.
 * (Any extra filtering can later be injected via config, but kept out of the stage UI.)
 */
export function useSessionsForDate({
  date,
  enabled,
  sessionTags,
}: UseSessionsForDateArgs): UseSessionsForDateResult {
  const [loading, setLoading] = useState<boolean>(true)
  const [sessions, setSessions] = useState<Session[]>([])

  const refresh = async () => {
    setLoading(true)
    try {
      const isoDay = formatDate(date, 'yyyy-MM-dd')
      const list = await apiGetSessionsByDate(isoDay)
      if (!list) {
        setSessions([])
        return
      }

      const nowMs = Date.now()
      const futureOnly = list.availabilities.filter((s) => new Date(s.dateTime).getTime() > nowMs)

      const filtered =
        sessionTags?.length
          ? futureOnly.filter((s) => sessionTags.every((tag) => s.tags?.includes(tag)))
          : futureOnly

      setSessions(filtered as unknown as Session[])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!enabled) return
    void refresh()
    // `refresh` intentionally not in deps to avoid re-creating on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, date, sessionTags])

  return { loading, sessions, refresh }
}
